/*
 * Copyright 2026 Klyph Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

package xyz.hyli.klyph

import androidx.compose.ui.util.fastFilter
import androidx.compose.ui.util.fastMap
import androidx.compose.ui.util.fastMapNotNull

private val COMMENTS_REGEX = Regex("""/\*[\s\S]*?\*/""")
private val FONT_FACE_REGEX = Regex("""@font-face\s*\{([^{}]+)\}""")
private val URL_REGEX = Regex("""url\((.*?)\)""")

/**
 * Parses a CSS string to extract @font-face rules and convert them into FontDescriptor objects.
 *
 * This function directly creates UrlFontDescriptor objects from CSS @font-face rules,
 * skipping intermediate representations. It automatically resolves relative URLs against the base URL if provided.
 *
 * @param css The CSS content as a string.
 * @param baseUrl Optional base URL for resolving relative URLs in the CSS. If provided, all relative URLs in src descriptors will be resolved against this base URL.
 * @return A list of [UrlFontDescriptor] objects found in the CSS.
 */
fun parseCssToDescriptors(css: String, baseUrl: String? = null): List<UrlFontDescriptor> {
    // TODO: Handle @import rules in CSS files
    // https://npm.webcache.cn/misans-webfont/misans-style.css

    // Strip all /* ... */ comments from the entire CSS string
    val cssNoComments = css.replace(COMMENTS_REGEX, "")

    val fontFaceBlocks = FONT_FACE_REGEX.findAll(cssNoComments)
    val descriptors = mutableListOf<UrlFontDescriptor>()

    for (block in fontFaceBlocks) {
        val content = block.groupValues[1].trim()

        // Split descriptors by semicolon
        val cssDescriptors = content.split(';').fastMap { it.trim() }.fastFilter { it.isNotEmpty() }

        // Create a map of descriptor key-value pairs
        val descriptorMap = cssDescriptors.fastMapNotNull {
            val parts = it.split(':', limit = 2)
            if (parts.size == 2) parts[0].trim().lowercase() to parts[1].trim() else null
        }.toMap()

        fun getValue(name: String): String? {
            return descriptorMap[name]?.removeSurrounding("\"")?.removeSurrounding("'")
        }

        // font-family is required
        val fontFamily = getValue("font-family") ?: continue

        // Extract font URL from src descriptor
        val srcValue = getValue("src") ?: continue
        val url = extractUrlFromSrc(srcValue) ?: continue

        // Resolve relative URL if base URL provided
        val resolvedUrl = if (baseUrl != null) {
            resolveUrl(baseUrl, url)
        } else {
            url
        }

        // Parse font weight
        val weight = parseFontWeight(getValue("font-weight"))

        // Parse font style
        val style = parseFontStyle(getValue("font-style"))

        // Parse unicode ranges
        val unicodeRanges = parseUnicodeRange(getValue("unicode-range"))

        descriptors.add(
            UrlFontDescriptor(
                url = resolvedUrl,
                fontFamily = fontFamily,
                weight = weight,
                style = style,
                unicodeRanges = unicodeRanges
            )
        )
    }

    return descriptors
}

/**
 * Extracts the first URL from a CSS src descriptor.
 *
 * Supports multiple formats:
 * - url("path/to/font.woff2")
 * - url('path/to/font.woff2')
 * - url(path/to/font.woff2)
 * - Multiple sources: url(...), url(...) (returns first URL)
 *
 * Local fonts (local(...)) are ignored for web compatibility.
 *
 * @param srcValue The src descriptor value from CSS.
 * @return The first URL found, or null if no URL is present.
 */
private fun extractUrlFromSrc(srcValue: String): String? {
    // Match url(...) pattern, using non-greedy match
    val match = URL_REGEX.find(srcValue) ?: return null

    // Extract URL and remove quotes if present
    return match.groupValues[1]
        .removeSurrounding("\"")
        .removeSurrounding("'")
        .trim()
}
